import { PmsType, ProgressNoteStatus, Provider, SiteTreatment, SiteTreatmentsDataService } from '@ffd/api-client';
import { DataState, FfdComponentStore, Loadable, routes } from '@libs/utils';
import { computed, inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  authActions,
  createAppointmentActions,
  patientActions,
  selectPatientPrimaryProvider,
  selectSiteSettingsPmsType,
  selectTeeth,
  selectUserProvider,
  selectUserSiteId,
} from '@libs/store';
import { Actions, ofType } from '@ngrx/effects';
import { EMPTY, filter, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  AddTreatmentClassification,
  mapTreatmentToTreatmentElement,
  RequiredInputArchConfiguration,
  RequiredInputData,
  RequiredInputQuadrantConfiguration,
  RequiredInputRangeConfiguration,
  RequiredInputToothConfiguration,
  RequiredInputType,
  Tooth,
} from '@libs/ui-lib';
import { TeethSelectorDialogComponent, TeethSelectorDialogData, TeethSelectorResult } from '../teeth-selector-dialog';
import { SurfaceSelectorDialogComponent, SurfaceSelectorDialogData, SurfaceSelectorResult } from '../surface-selector-dialog';
import { AddNewTreatmentData } from './add-treatment-modal.vm';
import { determineTeethSelectorMode, mapRequiredInputDialogResult } from './utils/utils';

export interface TreatmentsModalState {
  searchQuery: string;
  selectedStatus: ProgressNoteStatus;
  treatments: Loadable<SiteTreatment[]>;
  teeth: Tooth[];
  selectedClassificationId?: string;
  currentProvider?: Provider;
  addedTreatments: AddNewTreatmentData[];
}

export interface TreatmentsModalVM {
  searchQuery: string;
  selectedStatus: ProgressNoteStatus;
  classifications: Loadable<AddTreatmentClassification[]>;
  selectedClassification?: AddTreatmentClassification;
  teeth: Tooth[];
  currentProvider?: Provider;
  addedTreatments: AddNewTreatmentData[];
}

@Injectable()
export class AddTreatmentModalService extends FfdComponentStore<TreatmentsModalState, TreatmentsModalVM> implements OnDestroy {
  private readonly treatmentDataService = inject(SiteTreatmentsDataService);
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly actions$ = inject(Actions);

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  private readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);

  private requiredInputDialogRef: DialogRef<TeethSelectorResult, TeethSelectorDialogComponent> | null = null;
  private surfaceSelectorDialogRef: DialogRef<SurfaceSelectorResult, SurfaceSelectorDialogComponent> | null = null;

  constructor() {
    super({
      treatments: {
        state: DataState.Initial,
        data: [],
      },
      teeth: [],
      addedTreatments: [],
      searchQuery: '',
      selectedStatus: ProgressNoteStatus.P,
    });

    this.effect(() =>
      this.actions$.pipe(
        ofType(createAppointmentActions.onInit, authActions.onSiteSelected),
        tap((action) => {
          // refetch treatments on init and on site change
          const isSiteSelected = action.type === authActions.onSiteSelected.type;
          this.getTreatments({ forceRefetch: isSiteSelected });
        }),
      ),
    );
  }

  public override init(): void {
    const existingToothData = this.store.selectSignal(selectTeeth)()?.teethWithLayers || [];
    this.setTeeth(existingToothData);
    const currentProvider = structuredClone(this.store.selectSignal(selectUserProvider)());
    const currentPrimaryProvider = structuredClone(this.store.selectSignal(selectPatientPrimaryProvider)());
    const siteId = this.store.selectSignal(selectUserSiteId)();
    const isShowcase = siteId === routes.showcaseSiteId;
    if (isShowcase && currentProvider) {
      currentProvider.pmsId = `DR_${currentProvider?.firstName.charAt(0).toUpperCase()}${currentProvider?.lastName
        .charAt(0)
        .toUpperCase()}`;
    }
    this.patchState({ currentProvider: currentProvider || currentPrimaryProvider });
    this.getTreatments({ forceRefetch: false });
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.requiredInputDialogRef?.close();
  }

  protected override mapStateToViewModel(state: TreatmentsModalState): TreatmentsModalVM {
    const classifications = this.groupTreatments(state.treatments.data, state.searchQuery);

    return {
      searchQuery: state.searchQuery,
      selectedStatus: state.selectedStatus,
      classifications: {
        state: state.treatments.state,
        data: classifications,
        error: state.treatments.error,
      },
      selectedClassification: classifications?.find((c) => c.classification === state.selectedClassificationId) || classifications?.[0],
      currentProvider: state.currentProvider,
      addedTreatments: state.addedTreatments,
      teeth: state.teeth,
    };
  }

  private groupTreatments(treatments: SiteTreatment[] | undefined, searchQuery: string): AddTreatmentClassification[] | undefined {
    if (!treatments) {
      return undefined;
    }

    const treatmentsFilteredForOpenDental = this.isOpenDental()
      ? treatments.filter((treatment) => treatment.ffdRequiredInput === RequiredInputType.Mouth)
      : treatments;

    const filteredTreatments = treatmentsFilteredForOpenDental.filter((treatment) => {
      const lowerCaseSearchQuery = searchQuery.toLowerCase();
      const searchWords = lowerCaseSearchQuery.split(' ');
      return searchWords.every(
        (word) =>
          treatment.dentalCode.toLowerCase().includes(word) ||
          treatment.ffdShortDescription?.toLowerCase().includes(word) ||
          treatment.ffdLongDescription?.toLowerCase().includes(word) ||
          treatment.ffdCategory?.toLowerCase().includes(word) ||
          treatment.ffdSubCategory?.toLowerCase().includes(word),
      );
    });

    const groupedClassifications = (filteredTreatments.length > 0 ? filteredTreatments : []).reduce((acc, treatment) => {
      let classification = acc.find((c) => c.classification === treatment.ffdCategory);

      if (!classification) {
        classification = {
          classification: treatment.ffdCategory ?? '',
          categories: [],
          count: 0,
        };
        acc.push(classification);
      }

      let category = classification.categories.find((cat) => cat.name === (treatment.ffdSubCategory ?? ''));

      if (!category) {
        category = {
          name: treatment.ffdSubCategory ?? '',
          description: treatment.ffdSubCategoryDescriptions || '',
          treatments: [],
        };
        classification.categories.push(category);
      }

      category.treatments.push(mapTreatmentToTreatmentElement(treatment));
      category.treatments.sort((a, b) => a.id - b.id);
      classification.count++;

      return acc;
    }, [] as AddTreatmentClassification[]);

    const allTreatmentsClassification: AddTreatmentClassification = {
      classification: 'All',
      categories: groupedClassifications.flatMap((classification) => classification.categories),
      count: filteredTreatments.length,
    };

    return [allTreatmentsClassification, ...groupedClassifications];
  }

  public setSearchQuery = this.updater<string>((state, searchQuery): TreatmentsModalState => {
    return { ...state, searchQuery: searchQuery };
  });

  public setSelectedStatus = this.updater<ProgressNoteStatus>((state, status): TreatmentsModalState => {
    return {
      ...state,
      selectedStatus: status,
    };
  });

  public setSelectedClassificationId = this.updater<string>((state, classificationId): TreatmentsModalState => {
    return { ...state, selectedClassificationId: classificationId };
  });

  public setTeeth = this.updater<Tooth[]>((state, teeth): TreatmentsModalState => {
    return { ...state, teeth };
  });

  public setProvider = this.updater<Provider>((state, provider): TreatmentsModalState => {
    return { ...state, currentProvider: provider };
  });

  public addNewTreatments = this.updater<AddNewTreatmentData[]>((state, addedTreatments): TreatmentsModalState => {
    return { ...state, addedTreatments: [...state.addedTreatments, ...addedTreatments] };
  });
  public updateTreatment = this.updater<{ index: number; updatedTreatment: AddNewTreatmentData }>(
    (state, { index, updatedTreatment }): TreatmentsModalState => {
      const updatedTreatments = state.addedTreatments.map((treatment, i) =>
        i === index ? { ...treatment, ...updatedTreatment } : treatment,
      );
      return { ...state, addedTreatments: updatedTreatments };
    },
  );
  public removeTreatment = this.updater<number>((state, treatmentIndex): TreatmentsModalState => {
    return {
      ...state,
      addedTreatments: state.addedTreatments.filter((_, index) => index !== treatmentIndex),
    };
  });
  public resetTreatments = this.updater<void>((state): TreatmentsModalState => {
    return {
      ...state,
      addedTreatments: [],
    };
  });

  private readonly setSelectedTeeth = (treatment: AddNewTreatmentData): number[] => {
    if ((treatment.requiredInputData as RequiredInputRangeConfiguration)?.positions) {
      return (treatment.requiredInputData as RequiredInputRangeConfiguration).positions;
    }

    if ((treatment.requiredInputData as RequiredInputToothConfiguration)?.position) {
      return [(treatment.requiredInputData as RequiredInputToothConfiguration).position];
    }

    return [];
  };

  public readonly selectRequiredInput = this.effect<{ treatment: AddNewTreatmentData; treatmentIndex?: number; selectedTeeth?: number[] }>(
    ($) =>
      $.pipe(
        switchMap(({ treatment, treatmentIndex, selectedTeeth }) => {
          if (treatment.requiredInputType === undefined || treatment.requiredInputType === RequiredInputType.Mouth) {
            const newTreatments: AddNewTreatmentData[] = [{ ...treatment }];
            this.addNewTreatments(newTreatments);
            return EMPTY;
          }

          const isEditMode = treatmentIndex !== undefined;

          this.requiredInputDialogRef = this.dialog.open<TeethSelectorResult, TeethSelectorDialogData, TeethSelectorDialogComponent>(
            TeethSelectorDialogComponent,
            {
              backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
              disableClose: true,
              data: {
                teeth: this.viewModel().teeth,
                treatment,
                editing: isEditMode,
                mode: determineTeethSelectorMode(treatment.requiredInputType, isEditMode),
                selectedTeeth: selectedTeeth ?? this.setSelectedTeeth(treatment),
                selectedArches: (treatment.requiredInputData as RequiredInputArchConfiguration)?.arches,
                selectedQuadrants: (treatment.requiredInputData as RequiredInputQuadrantConfiguration)?.quadrants,
              },
            },
          );
          return this.requiredInputDialogRef.closed.pipe(
            tap((result) => {
              if (!result) {
                return;
              }

              const newTreatments: AddNewTreatmentData[] = mapRequiredInputDialogResult(treatment, result);

              if (treatment.requiredInputType === RequiredInputType.Surface && !isEditMode) {
                this.selectSurface({ treatment: newTreatments[0], selectedPositions: result.selectedTeeth, treatmentIndex });
                return;
              }

              if (isEditMode) {
                this.updateTreatment({ index: treatmentIndex, updatedTreatment: newTreatments[0] });
              } else {
                this.addNewTreatments(newTreatments);
              }
            }),
          );
        }),
      ),
  );

  public readonly selectSurface = this.effect<{ treatment: AddNewTreatmentData; selectedPositions: number[]; treatmentIndex?: number }>(
    ($) =>
      $.pipe(
        switchMap(({ treatment, selectedPositions, treatmentIndex }) => {
          const selectedTeeth = this.state().teeth.filter((tooth) => selectedPositions.includes(tooth.position));
          const isEditMode = treatmentIndex !== undefined;

          this.surfaceSelectorDialogRef = this.dialog.open<
            SurfaceSelectorResult,
            SurfaceSelectorDialogData,
            SurfaceSelectorDialogComponent
          >(SurfaceSelectorDialogComponent, {
            backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
            disableClose: true,
            data: {
              treatment,
              selectedTeeth,
              isEditMode,
            },
          });
          return this.surfaceSelectorDialogRef.closed.pipe(
            tap((result) => {
              if (!result) {
                return;
              }

              if (result.modifySelectionConfig) {
                this.selectRequiredInput({
                  treatment: result.modifySelectionConfig.treatment,
                  selectedTeeth: result.modifySelectionConfig.selectedTeeth,
                });
              }

              result.selectedTeethWithSurface.map((tooth) => {
                const newTreatment: AddNewTreatmentData = {
                  ...treatment,
                  requiredInputData: { position: tooth.position, surfaces: tooth.surfaces } as RequiredInputData,
                };

                if (isEditMode) {
                  this.updateTreatment({ index: treatmentIndex, updatedTreatment: newTreatment });
                } else {
                  this.addNewTreatments([newTreatment]);
                }
              });
            }),
          );
        }),
      ),
  );

  private readonly getTreatments = this.effect<{ forceRefetch: boolean }>(($) => {
    return $.pipe(
      filter(({ forceRefetch }) => {
        if (forceRefetch) {
          return true;
        }
        const currentState = this.state();
        return !(currentState.treatments.state === DataState.Fetched && (currentState.treatments.data ?? []).length > 0);
      }),
      tap(() => {
        this.patchState({ treatments: { state: DataState.Loading, data: [] } });
      }),
      switchMap(() => {
        const siteId = this.store.selectSignal(selectUserSiteId)();
        return this.treatmentDataService.getTreatmentsForSite(siteId ?? '').pipe(
          tapResponse(
            (response) => this.patchState({ treatments: { state: DataState.Fetched, data: response.treatments } }),
            (error) => {
              console.error('Error fetching treatments', error);
              this.patchState({ treatments: { state: DataState.Error, error: 'Error fetching treatments' } });
            },
          ),
        );
      }),
    );
  });

  public setAddedTreatments<T>(treatments: AddNewTreatmentData[]): void {
    this.store.dispatch(
      patientActions.onSetAddedTreatments({
        newTreatments: treatments,
        isShowcase: this.store.selectSignal(selectUserSiteId)() === routes.showcaseSiteId,
        provider: this.state().currentProvider ?? this.store.selectSignal(selectUserProvider)(),
      }),
    );
    this.resetTreatments();
  }
}
